import { useEffect, useState, useCallback } from 'react';
import {
  TrendingDown,
  Package,
  MoreHorizontal,
  Eye,
  Trash2,
  Undo2,
  AlertTriangle,
  BarChart3,
} from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { toast } from 'sonner';
import { RateBenchmarks } from '@/components/consumption/RateBenchmarks';
import { DemandForecast } from '@/components/consumption/DemandForecast';
import {
  PageHeader,
  SearchInput,
  DataTable,
  Column,
  MetricCard,
  KPIGrid,
  ConfirmDialog,
} from '@/components/common';
import {
  consumptionApi,
  ConsumptionRecord,
  CreateConsumptionDto,
  QueryConsumptionParams,
  ConsumptionDashboard,
  ConsumptionTrendItem,
} from '@/api/services/consumption.api';
import {
  jobsApi,
  type Job,
  inventoryApi,
  InventoryItem,
  locationsApi,
  Location,
} from '@/api/services';
import { getErrorMessage } from '@/api/client';
import { formatDate, formatDateTime, toTitleCase } from '@/lib/utils';
import { RATE_UNITS } from '@/lib/work-units';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import ConsumptionVarianceAnalysis from '@/pages/ConsumptionVarianceAnalysis';
import ConsumptionApprovals from '@/pages/ConsumptionApprovals';

interface RecordConsumptionFormData {
  // Core fields
  itemId: string;
  locationId: string;
  quantity: string;
  unit: string;
  workOrderId: string;
  notes: string;

  // OFS-specific fields
  plannedQuantity: string;
  varianceReason: string;
  jobRefId: string;
  operationPhase: string;
  consumptionRate: string;
  rateUnit: string;
  baseMetric: string;
  certificateNumbers: string;
}

const emptyFormData: RecordConsumptionFormData = {
  itemId: '',
  locationId: '',
  quantity: '',
  unit: 'EA',
  workOrderId: '',
  notes: '',
  // OFS fields
  plannedQuantity: '',
  varianceReason: '',
  jobRefId: '',
  operationPhase: '',
  consumptionRate: '',
  rateUnit: '',
  baseMetric: '',
  certificateNumbers: '',
};

export default function ConsumptionTracking() {
  const navigate = useNavigate();
  const [records, setRecords] = useState<ConsumptionRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState<ConsumptionDashboard | null>(null);
  const [_trends, setTrends] = useState<ConsumptionTrendItem[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });

  // OFS KPIs
  const [pendingApprovalsCount, setPendingApprovalsCount] = useState<number>(0);
  const [averageVariance, setAverageVariance] = useState<number>(0);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [locationFilter, setLocationFilter] = useState<string>('all');
  const [dateFilter, setDateFilter] = useState<string>('all');

  // Delete dialog
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    record?: ConsumptionRecord;
  }>({ open: false });
  const [deleting, setDeleting] = useState(false);

  // Detail sheet

  // Record consumption form
  const [formOpen, setFormOpen] = useState(false);
  const [formData, setFormData] = useState<RecordConsumptionFormData>(emptyFormData);
  const [saving, setSaving] = useState(false);

  // Returning material against a record. Backloading unused cement or chemicals
  // is routine, and issued-minus-returned is the figure that actually counts.
  const [returnDialog, setReturnDialog] = useState<{
    open: boolean;
    record?: ConsumptionRecord;
  }>({ open: false });
  const [returnQty, setReturnQty] = useState('');
  const [returnReason, setReturnReason] = useState('');
  const [returning, setReturning] = useState(false);

  // Jobs available to charge against. Only open ones — closing a job should
  // stop new material landing on it.
  const [openJobs, setOpenJobs] = useState<Job[]>([]);
  // In the URL, so a tab is linkable and the retired /consumption/variance and
  // /consumption/approvals routes can redirect straight into one. A previous
  // link wrote `?tab=` at a page that never read it and went nowhere; this
  // reads it.
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') ?? 'ledger';
  const setTab = (next: string) => setSearchParams(next === 'ledger' ? {} : { tab: next });

  const selectedJob = openJobs.find((j) => j.id === formData.jobRefId) ?? null;

  // Variance tracking
  const [variancePercentage, setVariancePercentage] = useState<number>(0);
  const [requiresApproval, setRequiresApproval] = useState<boolean>(false);

  // Reference data
  const [inventoryItems, setInventoryItems] = useState<InventoryItem[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [loadingRefs, setLoadingRefs] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params: QueryConsumptionParams = {
        search: searchQuery || undefined,
        page: pagination.page,
        limit: pagination.limit,
      };

      if (locationFilter !== 'all') {
        params.locationId = locationFilter;
      }

      if (dateFilter !== 'all') {
        const now = new Date();
        if (dateFilter === 'today') {
          params.startDate = new Date(now.setHours(0, 0, 0, 0)).toISOString();
        } else if (dateFilter === 'week') {
          const weekAgo = new Date();
          weekAgo.setDate(weekAgo.getDate() - 7);
          params.startDate = weekAgo.toISOString();
        } else if (dateFilter === 'month') {
          const monthAgo = new Date();
          monthAgo.setMonth(monthAgo.getMonth() - 1);
          params.startDate = monthAgo.toISOString();
        }
      }

      const [
        recordsResponse,
        dashboardResponse,
        trendsResponse,
        pendingApprovalsResponse,
        varianceResponse,
      ] = await Promise.all([
        consumptionApi.getAll(params),
        consumptionApi.getDashboard(),
        consumptionApi.getTrends({ periods: 14, interval: 'daily' }),
        consumptionApi.getPendingApprovals().catch(() => ({ data: [] })),
        consumptionApi
          .getVarianceAnalysis({
            startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
            endDate: new Date().toISOString(),
          })
          .catch(() => ({ data: { records: [], summary: { averageVariancePercentage: 0 } } })),
      ]);

      setRecords(recordsResponse.data.data);
      setPagination(recordsResponse.data.pagination);
      setDashboard(dashboardResponse.data);
      setTrends(trendsResponse.data);
      setPendingApprovalsCount(pendingApprovalsResponse.data.length);
      setAverageVariance(varianceResponse.data.summary.averageVariancePercentage);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [searchQuery, locationFilter, dateFilter, pagination.page, pagination.limit]);

  const fetchReferenceData = async () => {
    try {
      setLoadingRefs(true);
      const [itemsRes, locationsRes] = await Promise.all([
        inventoryApi.getAll({ limit: 500 }),
        locationsApi.getAll({ limit: 500 }),
      ]);
      setInventoryItems(itemsRes.data);
      setLocations(locationsRes.data);
    } catch (error) {
      console.error('Failed to load reference data:', error);
    } finally {
      setLoadingRefs(false);
    }
  };

  useEffect(() => {
    jobsApi
      .getAll({ limit: 200 })
      .then((res) =>
        setOpenJobs(res.data.filter((j) => j.status === 'PLANNED' || j.status === 'ACTIVE'))
      )
      .catch(() => setOpenJobs([]));
  }, []);

  useEffect(() => {
    fetchData();
    fetchReferenceData();
  }, [fetchData]);

  // Calculate variance in real-time
  useEffect(() => {
    const planned = parseFloat(formData.plannedQuantity);
    const actual = parseFloat(formData.quantity);

    if (planned && actual && planned > 0) {
      const variance = ((actual - planned) / planned) * 100;
      setVariancePercentage(variance);
      setRequiresApproval(Math.abs(variance) > 10);
    } else {
      setVariancePercentage(0);
      setRequiresApproval(false);
    }
  }, [formData.plannedQuantity, formData.quantity]);

  const handleRecordReturn = async () => {
    const record = returnDialog.record;
    const quantity = Number(returnQty);
    if (!record || !quantity || quantity <= 0) return;
    try {
      setReturning(true);
      await consumptionApi.recordReturn(record.id, {
        quantity,
        reason: returnReason || undefined,
      });
      toast.success(`Returned ${quantity} ${record.unit} to stock`);
      setReturnDialog({ open: false });
      setReturnQty('');
      setReturnReason('');
      await fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setReturning(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.record) return;

    try {
      setDeleting(true);
      await consumptionApi.delete(deleteDialog.record.id);
      toast.success('Consumption record deleted successfully');
      setDeleteDialog({ open: false });
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setDeleting(false);
    }
  };

  const handleOpenForm = () => {
    setFormData(emptyFormData);
    setFormOpen(true);
  };

  const handleItemChange = (itemId: string) => {
    const item = inventoryItems.find((i) => i.id === itemId);
    setFormData((prev) => ({
      ...prev,
      itemId,
      unit: item?.unit || 'EA',
    }));
  };

  const handleSubmit = async () => {
    if (!formData.itemId || !formData.locationId || !formData.quantity) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Validate variance reason if required
    if (requiresApproval && !formData.varianceReason) {
      toast.error('Variance reason is required for variances > 10%');
      return;
    }

    try {
      setSaving(true);
      const dto: CreateConsumptionDto = {
        // Core fields
        itemId: formData.itemId,
        locationId: formData.locationId,
        quantity: parseFloat(formData.quantity),
        unit: formData.unit,
        workOrderId: formData.workOrderId || undefined,
        notes: formData.notes || undefined,

        // OFS-specific fields
        plannedQuantity: formData.plannedQuantity
          ? parseFloat(formData.plannedQuantity)
          : undefined,
        varianceReason: formData.varianceReason || undefined,
        jobRefId: formData.jobRefId || undefined,
        operationPhase: formData.operationPhase || undefined,
        consumptionRate: formData.consumptionRate
          ? parseFloat(formData.consumptionRate)
          : undefined,
        rateUnit: formData.rateUnit || undefined,
        baseMetric: formData.baseMetric ? parseFloat(formData.baseMetric) : undefined,
        certificateNumbers: formData.certificateNumbers
          ? formData.certificateNumbers
              .split(',')
              .map((c) => c.trim())
              .filter(Boolean)
          : undefined,
      };

      const result = await consumptionApi.record(dto);

      if (result.data.requiresApproval) {
        toast.info('Consumption recorded. Awaiting supervisor approval due to variance.');
      } else {
        toast.success('Consumption recorded successfully');
      }

      setFormOpen(false);
      setFormData(emptyFormData);
      setVariancePercentage(0);
      setRequiresApproval(false);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const columns: Column<ConsumptionRecord>[] = [
    {
      key: 'item',
      header: 'Item',
      sortable: true,
      accessorFn: (row) => row.item.name,
      render: (row: ConsumptionRecord) => (
        <div>
          <p className="font-medium">{row.item.name}</p>
          <p className="text-xs text-muted-foreground">{row.item.sku}</p>
        </div>
      ),
    },
    {
      key: 'location',
      header: 'Location',
      sortable: true,
      accessorFn: (row) => row.location.name,
      render: (row: ConsumptionRecord) => <span>{row.location.name}</span>,
    },
    {
      key: 'quantity',
      header: 'Consumed',
      sortable: true,
      render: (row: ConsumptionRecord) => {
        // Net, with the issue shown underneath when some of it came back.
        // Eighteen records carry a return and the column showed the gross
        // figure for all of them, which is the same number every analytic
        // used to report.
        const returned = row.returnedQuantity ?? 0;
        return (
          <div>
            <span className="font-medium">
              {row.quantity - returned} {row.unit}
            </span>
            {returned > 0 && (
              <p className="text-xs text-muted-foreground">
                {row.quantity} issued, {returned} returned
              </p>
            )}
          </div>
        );
      },
    },
    {
      // Quantity alone hides the two things that make this an OFS record rather
      // than a stores issue: what it was charged to, and how it compared to plan.
      key: 'jobName',
      header: 'Job',
      render: (row: ConsumptionRecord) => (
        <div className="min-w-0">
          <p className="truncate text-sm font-medium">{row.jobId || '—'}</p>
          {row.jobName && <p className="truncate text-xs text-muted-foreground">{row.jobName}</p>}
        </div>
      ),
    },
    {
      key: 'operationPhase',
      header: 'Phase',
      render: (row: ConsumptionRecord) => (
        <span className="text-sm">
          {row.operationPhase ? (
            toTitleCase(row.operationPhase)
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </span>
      ),
    },
    {
      key: 'variancePercentage',
      header: 'Variance',
      sortable: true,
      render: (row: ConsumptionRecord) => {
        if (row.variancePercentage == null) return <span className="text-muted-foreground">—</span>;
        const over = row.variancePercentage > 0;
        return (
          <div className="text-sm">
            <span
              className={
                Math.abs(row.variancePercentage) <= 10
                  ? 'text-status-success-foreground'
                  : over
                    ? 'text-status-destructive-foreground'
                    : 'text-status-warning-foreground'
              }
            >
              {over ? '+' : ''}
              {row.variancePercentage.toFixed(1)}%
            </span>
            {(row.returnedQuantity ?? 0) > 0 && (
              <p className="text-xs text-muted-foreground">
                {row.returnedQuantity} {row.unit} returned
              </p>
            )}
          </div>
        );
      },
    },
    {
      key: 'consumedBy',
      header: 'Consumed By',
      render: (row: ConsumptionRecord) => row.consumedBy.fullName,
    },
    {
      key: 'consumedAt',
      header: 'Date',
      sortable: true,
      render: (row: ConsumptionRecord) => <span>{formatDateTime(row.consumedAt)}</span>,
    },
    {
      key: 'workOrder',
      header: 'Work Order',
      render: (row: ConsumptionRecord) =>
        row.workOrder ? (
          <span className="text-sm">{row.workOrder.workOrderNumber}</span>
        ) : (
          <span className="text-muted-foreground">-</span>
        ),
    },
    {
      key: 'actions',
      header: '',
      render: (row: ConsumptionRecord) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" aria-label="More actions">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => navigate(`/consumption/${row.id}`)}>
              <Eye className="mr-2 h-4 w-4" />
              View Details
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => {
                setReturnQty('');
                setReturnReason('');
                setReturnDialog({ open: true, record: row });
              }}
            >
              <Undo2 className="mr-2 h-4 w-4" />
              Record return
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-destructive"
              onClick={() => setDeleteDialog({ open: true, record: row })}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <div className="space-y-5">
      {/* The header used to carry two buttons that navigated to sibling pages
          and a third that opened the form — so the module's own structure was
          expressed as navigation, and a reader had to visit three URLs to see
          one thing. The tabs below do that job; the header keeps only the
          action that creates something. */}
      <PageHeader
        title="Consumption"
        description="Material issued to jobs and wells, measured against the quantity planned for each."
        actions={
          <Button onClick={handleOpenForm}>
            <TrendingDown className="mr-2 h-4 w-4" />
            Record Consumption
          </Button>
        }
      />

      {/* Dashboard Metrics */}
      <KPIGrid>
        {/* The window is stated, and an empty one says when consumption was last
            recorded. A tile reading 0 with no period and no context reads as a
            broken module rather than a quiet month. */}
        <MetricCard
          label="Records, last 30 days"
          value={dashboard?.summary.month.count || 0}
          icon={<TrendingDown className="h-5 w-5" />}
          sentiment="info"
          subtitle={
            (dashboard?.summary.month.count ?? 0) === 0 && dashboard?.lastRecordedAt
              ? `Nothing since ${formatDate(dashboard.lastRecordedAt)}`
              : `${dashboard?.summary.month.quantity?.toFixed(0) || 0} units consumed`
          }
        />
        <MetricCard
          label="Average Variance"
          value={`${averageVariance > 0 ? '+' : ''}${averageVariance.toFixed(1)}%`}
          icon={<BarChart3 className="h-5 w-5" />}
          sentiment={averageVariance > 10 ? 'danger' : averageVariance > 5 ? 'warning' : 'success'}
          trend={{
            direction: averageVariance > 0 ? 'up' : averageVariance < 0 ? 'down' : 'neutral',
            value:
              averageVariance > 0
                ? 'Over planned'
                : averageVariance < 0
                  ? 'Under planned'
                  : 'On target',
          }}
        />
        <div className="cursor-pointer" onClick={() => navigate('/consumption/approvals')}>
          <MetricCard
            label="Pending Approvals"
            value={pendingApprovalsCount}
            icon={<AlertTriangle className="h-5 w-5" />}
            sentiment={pendingApprovalsCount > 0 ? 'warning' : 'success'}
            trend={{
              direction: pendingApprovalsCount > 0 ? 'up' : 'neutral',
              value: pendingApprovalsCount > 0 ? 'Requires attention' : 'All approved',
            }}
          />
        </div>
        <MetricCard
          label="Top item, last 30 days"
          value={dashboard?.topItems[0]?.name || 'None'}
          icon={<Package className="h-5 w-5" />}
          sentiment="neutral"
          trend={
            dashboard?.topItems[0]
              ? {
                  direction: 'neutral',
                  value: `${dashboard.topItems[0].quantity} ${dashboard.topItems[0].unit}`,
                }
              : undefined
          }
        />
      </KPIGrid>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList>
          <TabsTrigger value="ledger">Ledger</TabsTrigger>
          <TabsTrigger value="variance">Against plan</TabsTrigger>
          <TabsTrigger value="forecast">Forecast</TabsTrigger>
          <TabsTrigger value="approvals">
            Approvals
            {pendingApprovalsCount > 0 && (
              <span className="ml-2 rounded-full bg-status-destructive px-2 py-0.5 text-xs font-bold text-white">
                {pendingApprovalsCount}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="ledger" className="mt-6 space-y-5">
          <DataTable
            onRowClick={(record) => navigate(`/consumption/${record.id}`)}
            columns={columns}
            data={records}
            loading={loading}
            toolbar={
              <>
                <SearchInput
                  value={searchQuery}
                  onChange={setSearchQuery}
                  placeholder="Search items..."
                  className="w-full sm:w-64"
                />
                <Select value={locationFilter} onValueChange={setLocationFilter}>
                  <SelectTrigger className="w-full sm:w-48">
                    <SelectValue placeholder="All Locations" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Locations</SelectItem>
                    {locations.map((location) => (
                      <SelectItem key={location.id} value={location.id}>
                        {location.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={dateFilter} onValueChange={setDateFilter}>
                  <SelectTrigger className="w-full sm:w-48">
                    <SelectValue placeholder="All Time" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Time</SelectItem>
                    <SelectItem value="today">Today</SelectItem>
                    <SelectItem value="week">Last 7 Days</SelectItem>
                    <SelectItem value="month">Last 30 Days</SelectItem>
                  </SelectContent>
                </Select>
              </>
            }
            pagination
            exportable
            columnToggle
          />
        </TabsContent>

        <TabsContent value="variance" className="mt-6">
          <ConsumptionVarianceAnalysis embedded />
        </TabsContent>

        <TabsContent value="forecast" className="mt-6 space-y-5">
          {/* What we expect to burn, and what the rates behind it were observed
              at. They belong together: a projection nobody can trace back to an
              observation is a number to be argued with, not acted on. */}
          <DemandForecast items={inventoryItems} />
          <RateBenchmarks />
        </TabsContent>

        <TabsContent value="approvals" className="mt-6">
          <ConsumptionApprovals embedded />
        </TabsContent>
      </Tabs>

      {/* Record Consumption Dialog */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Record Consumption</DialogTitle>
            <DialogDescription>Record material consumption from inventory</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Core Fields */}
            <div className="space-y-2">
              <Label htmlFor="item">Item *</Label>
              <Select
                value={formData.itemId}
                onValueChange={handleItemChange}
                disabled={loadingRefs}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select item" />
                </SelectTrigger>
                <SelectContent>
                  {inventoryItems.map((item) => (
                    <SelectItem key={item.id} value={item.id}>
                      {item.name} ({item.sku})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="location">Location *</Label>
              <Select
                value={formData.locationId}
                onValueChange={(v) => setFormData((prev) => ({ ...prev, locationId: v }))}
                disabled={loadingRefs}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select location" />
                </SelectTrigger>
                <SelectContent>
                  {locations.map((loc) => (
                    <SelectItem key={loc.id} value={loc.id}>
                      {loc.name} ({loc.code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* OFS-Specific: Job/Well Context */}
            <div className="border-t pt-4 space-y-4">
              <h3 className="text-base font-semibold">Job</h3>
              <div className="space-y-2">
                <Label htmlFor="jobRefId">Charge to job</Label>
                <Select
                  value={formData.jobRefId}
                  onValueChange={(value) => setFormData((prev) => ({ ...prev, jobRefId: value }))}
                >
                  <SelectTrigger id="jobRefId">
                    <SelectValue placeholder="Select a job" />
                  </SelectTrigger>
                  <SelectContent>
                    {openJobs.length === 0 ? (
                      <div className="px-2 py-3 text-sm text-muted-foreground">
                        No open jobs. Create one under Jobs first.
                      </div>
                    ) : (
                      openJobs.map((job) => (
                        <SelectItem key={job.id} value={job.id}>
                          {job.jobNumber} — {job.name}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                {/* The customer's references travel with the job, so whoever records
                    consumption never retypes a well number or an AFE. */}
                {selectedJob && (
                  <p className="text-xs text-muted-foreground">
                    {[
                      selectedJob.customerName,
                      selectedJob.well?.name,
                      selectedJob.afeNumber && `AFE ${selectedJob.afeNumber}`,
                    ]
                      .filter(Boolean)
                      .join(' · ') || 'No customer references recorded on this job'}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="operationPhase">Operation Phase</Label>
                <Select
                  value={formData.operationPhase}
                  onValueChange={(v) => setFormData((prev) => ({ ...prev, operationPhase: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select operation phase" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="DRILLING">Drilling</SelectItem>
                    <SelectItem value="COMPLETION">Completion</SelectItem>
                    <SelectItem value="PRODUCTION">Production</SelectItem>
                    <SelectItem value="MAINTENANCE">Maintenance</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* OFS-Specific: Variance Tracking */}
            <div className="border-t pt-4 space-y-4">
              <h3 className="text-base font-semibold">Planned vs. Actual Tracking</h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="plannedQuantity">Planned Quantity</Label>
                  <Input
                    id="plannedQuantity"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.plannedQuantity}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, plannedQuantity: e.target.value }))
                    }
                    placeholder="0"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="quantity">Actual Quantity *</Label>
                  <Input
                    id="quantity"
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={formData.quantity}
                    onChange={(e) => setFormData((prev) => ({ ...prev, quantity: e.target.value }))}
                    placeholder="0"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="unit">Unit</Label>
                  <Input id="unit" value={formData.unit} disabled />
                </div>
              </div>

              {/* Variance Warning */}
              {formData.plannedQuantity && formData.quantity && (
                <div className="space-y-2">
                  {requiresApproval ? (
                    <Alert variant="destructive">
                      <AlertTriangle className="h-4 w-4" />
                      <AlertTitle>Approval Required</AlertTitle>
                      <AlertDescription>
                        Variance of {variancePercentage.toFixed(1)}% exceeds 10% threshold.
                        Supervisor approval will be required.
                      </AlertDescription>
                    </Alert>
                  ) : (
                    <Alert>
                      <AlertDescription>
                        Variance: {variancePercentage > 0 ? '+' : ''}
                        {variancePercentage.toFixed(1)}% (within acceptable range)
                      </AlertDescription>
                    </Alert>
                  )}
                </div>
              )}

              {requiresApproval && (
                <div className="space-y-2">
                  <Label htmlFor="varianceReason">Variance Reason *</Label>
                  <Select
                    value={formData.varianceReason}
                    onValueChange={(v) => setFormData((prev) => ({ ...prev, varianceReason: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select reason" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="FIELD_CONDITION_CHANGE">Field Condition Change</SelectItem>
                      <SelectItem value="EQUIPMENT_ISSUE">Equipment Issue</SelectItem>
                      <SelectItem value="NORMAL_SHRINKAGE">Normal Shrinkage</SelectItem>
                      <SelectItem value="THEFT">Theft/Loss</SelectItem>
                      <SelectItem value="OTHER">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {/* OFS-Specific: Rate-Based Consumption */}
            <div className="border-t pt-4 space-y-4">
              <h3 className="text-base font-semibold">Rate-Based Consumption (Optional)</h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="consumptionRate">Consumption Rate</Label>
                  <Input
                    id="consumptionRate"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.consumptionRate}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, consumptionRate: e.target.value }))
                    }
                    placeholder="e.g., 1.2"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="rateUnit">Rate Unit</Label>
                  <Select
                    value={formData.rateUnit}
                    onValueChange={(v) => setFormData((prev) => ({ ...prev, rateUnit: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select unit" />
                    </SelectTrigger>
                    <SelectContent>
                      {RATE_UNITS.map((u) => (
                        <SelectItem key={u.value} value={u.value}>
                          {u.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="baseMetric">Base Metric</Label>
                  <Input
                    id="baseMetric"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.baseMetric}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, baseMetric: e.target.value }))
                    }
                    placeholder="e.g., 500 ft"
                  />
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Example: 0.09 bbl/ft × 500 ft drilled = 45 bbl consumed
              </p>
            </div>

            {/* OFS-Specific: Certification */}
            <div className="border-t pt-4 space-y-4">
              <h3 className="text-base font-semibold">Certification (Optional)</h3>
              <div className="space-y-2">
                <Label htmlFor="certificateNumbers">Certificate Numbers</Label>
                <Input
                  id="certificateNumbers"
                  value={formData.certificateNumbers}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, certificateNumbers: e.target.value }))
                  }
                  placeholder="Comma-separated certificate numbers"
                />
                <p className="text-xs text-muted-foreground">
                  Example: CERT-001, CERT-002, MTR-12345
                </p>
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
                placeholder="Optional notes about this consumption"
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setFormOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={saving}>
              {saving ? 'Recording...' : 'Record Consumption'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Sheet */}
      {/* The detail sheet lived here. It is a page now, at
          /consumption/:id — the same one Consumption Approvals opens. */}

      {/* Delete Confirmation */}
      <Dialog
        open={returnDialog.open}
        onOpenChange={(open) => !open && setReturnDialog({ open: false })}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Record a return</DialogTitle>
            <DialogDescription>
              {returnDialog.record && (
                <>
                  {returnDialog.record.item?.sku} — {returnDialog.record.quantity}{' '}
                  {returnDialog.record.unit} was consumed
                  {(returnDialog.record.returnedQuantity ?? 0) > 0 &&
                    `, ${returnDialog.record.returnedQuantity} already returned`}
                  . Material goes back to {returnDialog.record.location?.name}.
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="returnQty">Quantity returned</Label>
              <Input
                id="returnQty"
                type="number"
                min={0}
                max={
                  returnDialog.record
                    ? returnDialog.record.quantity - (returnDialog.record.returnedQuantity ?? 0)
                    : undefined
                }
                value={returnQty}
                onChange={(e) => setReturnQty(e.target.value)}
                placeholder="0"
              />
              {returnDialog.record && (
                <p className="text-xs text-muted-foreground">
                  {returnDialog.record.quantity - (returnDialog.record.returnedQuantity ?? 0)}{' '}
                  {returnDialog.record.unit} still outstanding. Cost, variance and rate are all
                  restated on the net.
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="returnReason">Reason</Label>
              <Input
                id="returnReason"
                value={returnReason}
                onChange={(e) => setReturnReason(e.target.value)}
                placeholder="Backloaded unused"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setReturnDialog({ open: false })}
              disabled={returning}
            >
              Cancel
            </Button>
            <Button onClick={handleRecordReturn} disabled={returning || !Number(returnQty)}>
              {returning ? 'Recording…' : 'Record return'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={deleteDialog.open}
        onOpenChange={(open) => setDeleteDialog((prev) => ({ ...prev, open }))}
        title="Delete Consumption Record"
        description="Are you sure you want to delete this consumption record? The inventory will be restored."
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={handleDelete}
        loading={deleting}
      />
    </div>
  );
}
